import { httpRequest } from '@/api/HttpRequest';
import { create, StateCreator } from 'zustand';
import { devtools } from 'zustand/middleware';
import { SessionDefaultState, SessionType } from '../types/SessionType';

type SessionStoreState = {
  session: SessionType;
  loading: boolean;
  error: boolean;
  login: (email: string, orderId: string) => Promise<boolean>;
  logout: () => void;
  getUserRoles: () => Promise<string[]>;
};

const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL ?? '';

const useSessionStore = create<SessionStoreState>(

  devtools<SessionStoreState>(
    (set, get) => {
    const setLoading = (loading: boolean) => set((state) => ({ ...state, loading }));
    const setError = (error: boolean) => set((state) => ({ ...state, error }));
    const updateSession = (session: SessionType) => set(() => ({ session }));

    const getUserRoles = async () => {
      setLoading(true);
      try {
        const result = await httpRequest('GET', `${apiBaseUrl}/session?extras=roles`);
        if (result.status === 200) {
          const session: SessionType = await result.json();
          setLoading(false);
          return session.roles;
        } else {
          console.warn('Unexpected status code', result.status);
          setError(true);
          setLoading(false);
        }
      } catch (error) {
        console.warn(error);
        setError(true);
        setLoading(false);
      }
      return [];
    };

    const login = async (email: string, orderId: string) => {
      setLoading(true);
      try {
        const body = { username: email, order_id: orderId };
        const result = await httpRequest('POST', `${apiBaseUrl}/session`, body);
        if (result.status === 200) {
          const session: SessionType = await result.json();
          session.login_order_id = orderId;
          if (typeof window !== 'undefined' && window.localStorage) {
            localStorage.setItem('sid', session.id);
            localStorage.setItem('lastLoginDate', new Date().toISOString());
          }
          updateSession(session);
          setLoading(false);
          return true;
        }
      } catch (error) {
        console.warn(error);
        setError(true);
      } finally {
        setLoading(false);
      }
      return false;
    };

    const logout = () => {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.removeItem('sid');
        updateSession(SessionDefaultState);
      }
    };

    const loadSessionFromStorage = () => {
      if (typeof window !== 'undefined' && window.localStorage) {
        const sid = localStorage.getItem('sid');
        if (sid) {
          httpRequest('GET', `${apiBaseUrl}/session?extras=login_order_id`, false, sid).then(
            async (result) => {
              if (result.status === 200) {
                const session: SessionType = await result.json();
                if (session?.id === '') {
                  logout();
                } else {
                  updateSession(session);
                }
              }
            },
            (error) => {
              console.warn(error);
            }
          );
        }
      }
    };

    loadSessionFromStorage();

    return {
      session: SessionDefaultState,
      loading: false,
      error: false,
      login,
      logout,
      getUserRoles,
    };
  },
) as unknown as StateCreator<SessionStoreState>
);

export default useSessionStore;
